import stringHelper from './stringHelper';

/**
 * Shared utilities for sitemap models
 * Used by both CitySitemap and StateSitemap models
 */

// Type definitions
type PhraseMapKey =
  | 'apartments-for-rent'
  | 'houses-for-rent'
  | 'condos-for-rent'
  | 'affordable-apartments-for-rent'
  | 'for-rent-by-owner'
  | 'rooms-for-rent'
  | 'sublets'
  | 'student-housing';

interface AreaGroup {
  id?: string;
  name?: string;
  uriV2?: string;
  listingCount?: number;
  areaType?: string;
  [key: string]: unknown;
}

interface MappedAreaGroup {
  id: string;
  resourceId: string | null;
  name: string;
  uriV2: string;
  listingCount: number;
  phrase: string;
  [key: string]: unknown;
}

export interface PropertyTypeAccumulator {
  [key: string]: Array<MappedAreaGroup>;
}

interface AreaTypeMap {
  [areaType: string]: string;
}

export interface PropertyTypeItem {
  areaType?: string;
  areaGroups?: Array<AreaGroup>;
  header?: PhraseMapKey;
}

export const extractResourceIdFromUriV2 = (uriV2: unknown): string | null => {
  if (!uriV2 || typeof uriV2 !== 'string') return null;
  // Extract resourceId from "/san-francisco-ca/apartments-for-rent" -> "san-francisco-ca"
  const segments = uriV2.split('/');
  return segments.length >= 2 ? segments[1] : null;
};

export const areaGroupMapper = (areaGroups: unknown, header: PhraseMapKey): Array<MappedAreaGroup> => {
  if (!Array.isArray(areaGroups)) {
    return [];
  }

  return areaGroups
    .filter((areaGroup): areaGroup is AreaGroup => {
      // Only filter out areas with invalid or missing listing count data
      // Areas with 0 listings should be displayed (e.g., "0 apartments available")
      return (
        areaGroup &&
        typeof areaGroup === 'object' &&
        typeof (areaGroup as AreaGroup).listingCount === 'number' &&
        (areaGroup as AreaGroup).listingCount! >= 0
      );
    })
    .map((areaGroup: AreaGroup, index: number): MappedAreaGroup => {
      const resourceId = extractResourceIdFromUriV2(areaGroup.uriV2);
      // Generate fallback ID if not provided by API
      const id = areaGroup.id || resourceId || `${areaGroup.name || 'area'}-${index}`;

      // Ensure we have safe fallback values for required fields
      const safeName = areaGroup.name || 'Unknown Area';
      const safeUriV2 = areaGroup.uriV2 || '';
      // TypeScript knows listingCount is defined because of the filter, but we need to assert it
      const safeListingCount = areaGroup.listingCount!;

      return {
        ...areaGroup,
        id,
        resourceId,
        name: safeName,
        uriV2: safeUriV2,
        listingCount: safeListingCount,
        phrase: `${safeListingCount} ${stringHelper.getPhrase(safeListingCount, header)}`,
      };
    });
};

/**
 * Creates a property type reducer function with configurable area type mappings
 * @param {Object} areaTypeMap - Maps area types to property names (e.g., { city: 'cities', neighborhood: 'neighborhoods' })
 */
export const createPropertyTypeReducer = (
  areaTypeMap: AreaTypeMap,
): ((acc: PropertyTypeAccumulator, curr: PropertyTypeItem) => PropertyTypeAccumulator) => {
  return (acc: PropertyTypeAccumulator, curr: PropertyTypeItem): PropertyTypeAccumulator => {
    if (curr.areaType && areaTypeMap[curr.areaType] && curr.header) {
      acc[areaTypeMap[curr.areaType]] = areaGroupMapper(curr.areaGroups, curr.header);
    }
    return acc;
  };
};
